/**
 * The `Earnings` and `Quality score` screens, from EXP-003's one read, away from any rendering.
 *
 * Two designed screens over one operation, because the design draws them as two faces of the same
 * fact — what a courier did this week and how it is being judged. `15 WP14`'s own words:
 *
 *   - `Earnings` — `TZS 48,200` / `22 jobs · 31 h online · paid Friday`, with four rows and a
 *     `See statement` button.
 *   - `Quality score` — `4.8 rating` / `Last 100 jobs · shown from RAT-03`, with three metric rows
 *     and an informational note.
 *
 * ## The earnings screen is mostly an absence, and that is the finding rather than a shortfall
 *
 * **Nothing on this platform publishes what a courier has earned.** Swept and recorded as R-22:
 * FIN-06's commission publishes two operations and both are `/internal/`, which the gateway does not
 * expose; FIN-07 publishes `GET /v1/payouts/{payoutId}` and **no listing**; and no per-driver
 * earnings read exists anywhere in the published surface. So EXP-003 answers `earnings: null` with
 * an `earningsAbsence` naming which of three facts it is, and never a zero.
 *
 * That last part is the rule to hold on to. A zero is a number a courier acts on. `TZS 0` on this
 * screen is a courier concluding they worked for nothing, and there is no accounting behind it — so
 * the headline is the absence, drawn in the design's own `unknown` pill, and the four rows say which
 * of them could not be found rather than totalling to nothing.
 *
 * ## The quality screen is mostly real, and its one absence is the target
 *
 * RAT-03 answers the rating. DRV-07 answers one performance window per metric, with a numerator and
 * a denominator, so `86%` is published. `target 80%` is not: nothing publishes `aggregate.target`,
 * `aggregate.targetBasisPoints` or a metric's name as data. So each row carries its percentage and
 * the target is the absence.
 */

import { formatMoney } from "./money.ts";
import type { ChipClass } from "./shift-readiness.ts";

/** EXP-003's `earnings` response, as the generated client types it. */
export interface EarningsView {
  readonly tenantId: string;
  readonly driverId: string;
  readonly observedAt: string;
  readonly earnings: {
    readonly payoutId: string;
    readonly amountMinor: number;
    readonly currency: string;
    readonly state:
      | "CREATED"
      | "APPROVED"
      | "SUBMITTED"
      | "PROCESSING"
      | "PAID"
      | "FAILED"
      | "RETURNED"
      | "CANCELLED";
    readonly confirmed: boolean;
    readonly requestedAt: string;
    readonly updatedAt: string;
  } | null;
  readonly earningsAbsence?: "no_published_route" | "payout_not_requested" | "payout_unreadable";
  readonly rating: {
    readonly averageScoreBasisPoints: number;
    readonly observationCount: number;
    readonly lastEventAt: string;
    readonly confirmed: boolean;
  } | null;
  readonly metrics: readonly {
    readonly metric: string;
    readonly numerator: number | null;
    readonly denominator: number | null;
    readonly windowStart: string | null;
    readonly windowEnd: string | null;
    readonly updatedAt: string | null;
    readonly absence?: "no_window" | "forbidden" | "unavailable" | "not_requested";
  }[];
  readonly countsAreTotals: boolean;
  readonly countFloors: readonly {
    readonly layer: string;
    readonly service: string;
    readonly reason: "truncated" | "did_not_answer" | "bounded";
  }[];
  readonly metricLookups: number;
  readonly sources: readonly {
    readonly layer: string;
    readonly service: string;
    readonly status: "ok" | "forbidden" | "unauthorized" | "unavailable" | "timeout" | "invalid";
    readonly scopes: readonly string[];
    readonly code?: string;
    readonly truncated?: boolean;
  }[];
  readonly degraded: boolean;
}

/** A key/value row of the design's `rowsBox`, with absence carried rather than flattened. */
export interface MoneyRow {
  readonly key: string;
  /** The value, or `null` when the platform publishes nothing for it. */
  readonly value: string | null;
  /** Why there is no value, for a screen that must say which kind of nothing this is. */
  readonly because?: string;
}

/**
 * The money headline, which is `TZS 48,200` when there is money and the absence when there is not.
 *
 * `confirmed` is FIN-07's own flag rather than a derivation from `state`, and it is the one that
 * decides the chip below: `outcomeUncertain` means the provider has not told the platform what
 * happened, and a courier shown `PAID` over an uncertain payout has been told their money arrived
 * when nobody knows.
 */
export function earningsHeadline(view: EarningsView): {
  readonly headline: string;
  readonly isAbsence: boolean;
} {
  const payout = view.earnings;
  if (payout === null) {
    return { headline: earningsAbsenceWords(view.earningsAbsence), isAbsence: true };
  }
  return { headline: formatMoney(payout.amountMinor, payout.currency), isAbsence: false };
}

/**
 * Three facts, worded as three facts.
 *
 * Collapsing them into one sentence would lose the only actionable one: `payout_not_requested` is
 * the caller's to fix, where the other two are the platform's. Four of five `courierAbsence` values
 * meaning "we could not find out" and one meaning "nobody is carrying it" is the same shape of
 * distinction, and drawing those alike is a recorded incident on this platform.
 */
export function earningsAbsenceWords(absence: EarningsView["earningsAbsence"]): string {
  switch (absence) {
    case "no_published_route":
      return "Not published yet";
    case "payout_not_requested":
      return "No payout named";
    case "payout_unreadable":
      return "Could not be read";
    case undefined:
      return "Not published yet";
  }
}

/**
 * The chip on the earnings screen: `this week` in the design, with two honest departures.
 *
 * The design's chip is `['done','this week']` — a green pill over a figure. A green pill over an
 * absence would be the R-09 over-claim with money as its subject, so an absent or unconfirmed payout
 * takes the design's own `unknown` pill instead: dashed, mono, carrying a `?`. The word is this
 * build's and is recorded in Q-61.
 */
export function earningsChip(view: EarningsView): { class: ChipClass; label: string } {
  if (view.earnings === null) return { class: "unknown", label: "not published" };
  if (!view.earnings.confirmed) return { class: "unknown", label: "unconfirmed" };
  return { class: "done", label: "this week" };
}

/**
 * The four rows of the design's earnings screen, in its order and with its keys word for word.
 *
 * `Trip earnings`, `Busy-area bonus`, `Commission (FIN-06)` and `Next payout`. The first two and the
 * third have no published source at all — a bonus is not a published concept and commission is
 * `/internal/` only — so they carry their absence. `Next payout` is the one row a `payoutId` can
 * fill, and it then carries FIN-07's own `state` rather than the word `Friday`, because nothing
 * publishes a payout schedule.
 */
export function earningsRows(view: EarningsView): MoneyRow[] {
  const payout = view.earnings;
  return [
    {
      key: "Trip earnings",
      value: null,
      because: "No per-courier earnings read is published",
    },
    {
      key: "Busy-area bonus",
      value: null,
      because: "Nothing publishes a bonus line",
    },
    {
      key: "Commission (FIN-06)",
      value: null,
      because: "FIN-06 publishes only internal operations",
    },
    payout === null
      ? { key: "Next payout", value: null, because: earningsAbsenceWords(view.earningsAbsence) }
      : {
          key: "Next payout",
          value: `${formatMoney(payout.amountMinor, payout.currency)} · ${payout.state.toLowerCase()}`,
        },
  ];
}

/* ------------------------------------------------------------------------- the quality screen */

/**
 * `4.8 rating`, from RAT-03's integer.
 *
 * `averageScoreBasisPoints` is not basis points of a percentage here, and getting that wrong by a
 * factor of a hundred would be easy. RAT-03 computes it as
 * `floor((scoreNumerator * 10_000) / observationCount)` over facts whose `scoreUnits` are integers
 * `0..5`, so the value is the mean score times ten thousand: a 4.8 average is `48000`. Read from
 * `services/rating/quality-aggregation/src/quality.ts` rather than inferred from the field's name.
 *
 * One decimal place, which is what the design prints. The division is deliberately not money and
 * floating point is fine for it — a rating is a measurement rather than an amount — but it is done
 * on the integer rather than on a pre-divided float so the rounding happens once.
 */
export function ratingLabel(basisPoints: number): string {
  return (Math.round(basisPoints / 1000) / 10).toFixed(1);
}

/**
 * The quality screen's chip: `good standing` in the design.
 *
 * A stale aggregate is a number that was true at `lastEventAt` and may not be now, and RAT-03
 * publishes its own `stale` flag which EXP-003 inverts into `confirmed`. A stale rating therefore
 * takes the `unknown` pill rather than the green one. The design's stale treatment — it has one, for
 * the customer wallet — is a dashed pill and a sub-line naming the instant, and that is what this is.
 */
export function qualityChip(view: EarningsView): { class: ChipClass; label: string } {
  if (view.rating === null) return { class: "unknown", label: "no rating" };
  return view.rating.confirmed
    ? { class: "done", label: "good standing" }
    : { class: "unknown", label: "last known" };
}

/**
 * `Last 100 jobs · shown from RAT-03` — the design's sub-line, with its count made real.
 *
 * `observationCount` is a count, so R-04 applies: no listing on this platform publishes a total, and
 * EXP-003 carries `countsAreTotals` for exactly this. Where it is false the count is a floor and the
 * sub-line says `at least`, because `Last 100 jobs` over a floor of 100 is a claim the platform did
 * not make.
 */
export function qualitySubline(view: EarningsView): string {
  const rating = view.rating;
  if (rating === null) return "Nothing to show yet · RAT-03";
  const count = view.countsAreTotals
    ? `Last ${String(rating.observationCount)} jobs`
    : `At least ${String(rating.observationCount)} jobs`;
  return `${count} · shown from RAT-03`;
}

/**
 * One metric row: `Acceptance`, `86%`, and the target that has no source.
 *
 * The design writes `Acceptance` / `86% · target 80%`. The percentage is published —
 * `numerator / denominator` from DRV-07 — and the target is not: nothing publishes
 * `aggregate.metricName`, `aggregate.target` or `aggregate.targetBasisPoints`. So the row carries
 * the percentage and the target is absent, which is the one drawn element this screen loses.
 *
 * `denominator === 0` is a window with nothing observed in it, and `0/0` is not `0%`: a courier who
 * was offered no jobs has no acceptance rate rather than a rate of zero. It is an absence.
 */
export function metricRow(metric: EarningsView["metrics"][number]): MoneyRow {
  const key = metricLabel(metric.metric);
  if (metric.absence !== undefined) {
    return { key, value: null, because: metricAbsenceWords(metric.absence) };
  }
  const { numerator, denominator } = metric;
  if (numerator === null || denominator === null) {
    return { key, value: null, because: "DRV-07 published no window" };
  }
  if (denominator === 0) {
    return { key, value: null, because: "Nothing observed in this window" };
  }
  return { key, value: `${String(Math.round((numerator / denominator) * 100))}%` };
}

/**
 * A metric identifier as the design's row key.
 *
 * DRV-07 types `metric` as a free string in its path and publishes no set of them, so there is
 * nothing to enumerate from the contract. The three the design names are mapped to its exact words
 * and anything else is shown as the service named it — which is better than a prettified guess,
 * because a metric this build has never heard of is a metric whose meaning it must not assert.
 */
export function metricLabel(metric: string): string {
  switch (metric) {
    case "acceptance":
      return "Acceptance";
    case "completion":
      return "Completion";
    case "on_time_pickup":
      return "On-time pickup";
    default:
      return metric;
  }
}

/** Four facts, worded as four, for the same reason as the money ones. */
export function metricAbsenceWords(
  absence: NonNullable<EarningsView["metrics"][number]["absence"]>,
): string {
  switch (absence) {
    case "no_window":
      return "Nothing observed yet";
    case "forbidden":
      return "You do not have permission to see this";
    case "unavailable":
      return "DRV-07 did not answer";
    case "not_requested":
      return "Not asked for on this read";
  }
}

/**
 * The three metrics the design's quality screen names, in its order.
 *
 * Sent to EXP-003 as its `metrics` query parameter, which is comma-separated because DRV-07
 * publishes no set to enumerate. The names are the design's rows turned into identifiers; if DRV-07
 * knows them by other names every row comes back `no_window`, which is visible rather than silent.
 */
export const DESIGNED_METRICS = ["acceptance", "completion", "on_time_pickup"] as const;
